import moment from 'moment/moment';
import React from 'react';
import { 
  FiX, 
  FiDownload, 
  FiUser, 
  FiTag, 
  FiCalendar, 
  FiClock, 
  FiDollarSign,
  FiCheckCircle,
  FiXCircle,
  FiArrowUp,
  FiRefreshCw,
  FiFileText,
  FiPrinter,
  FiShare2,
  FiEye,
  FiTrendingUp,
  FiTrendingDown,
  FiAlertCircle
} from "react-icons/fi";
import { 
  MdOutlineFileDownload, 
  MdOutlinePerson,
  MdOutlineAttachMoney,
  MdOutlineCalendarToday,
  MdOutlineAccessTime,
  MdOutlineCheckCircle,
  MdOutlineCancel,
  MdOutlinePending,
  MdOutlineRefresh,
  MdOutlineClose
} from "react-icons/md";
import { IoDocumentTextOutline, IoDownloadOutline, IoCloseOutline } from "react-icons/io5";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ReactPaginate from 'react-paginate';

const UserPaymentDetails = ({ 
  pageNo, 
  transactions, 
  onClose, 
  title, 
  total, 
  size, 
  handlePageClick, 
  userInfo 
}) => {
  const tableHeaders = ["S.No.", "Transaction ID", "Payment Type", "User Name", "Date & Time", "Status", "Amount"];

  const handleDownloadPDF = () => {
    const doc = new jsPDF();

    // Add title
    doc.setFontSize(16);
    doc.text(title, 14, 15);

    // Add user name if available
    if (userInfo?.fullName) {
      doc.setFontSize(12);
      doc.text(`User: ${userInfo.fullName}`, 14, 22);
    }

    // Add export date
    doc.setFontSize(10);
    doc.text(`Exported on: ${moment().format('YYYY-MM-DD HH:mm')}`, 14, 28);

    // Define table data
    const tableData = transactions.map((item, index) => [
      index + 1,
      `#${item.id || item._id?.slice(-6) || ''}`,
      item.type || '-',
      item.user?.fullName || '-',
      moment(item.createdAt).format('YYYY-MM-DD HH:mm'),
      item.status || '-',
      item.amount?.toFixed(2) || '0.00'
    ]);

    // Generate table
    autoTable(doc, {
      head: [tableHeaders],
      body: tableData,
      startY: 35,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [113, 94, 254], textColor: 255 },
      alternateRowStyles: { fillColor: [249, 250, 251] },
    });

    // Save PDF
    doc.save(`${title.replace(/\s+/g, '_')}_${moment().format('YYYYMMDD')}.pdf`);
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      'SUCCESS': { 
        icon: FiCheckCircle, 
        color: 'text-emerald-700 bg-emerald-50 border-emerald-200', 
        label: 'Success' 
      },
      'COMPLETED': { 
        icon: FiCheckCircle, 
        color: 'text-emerald-700 bg-emerald-50 border-emerald-200', 
        label: 'Completed' 
      },
      'PENDING': { 
        icon: FiClock, 
        color: 'text-amber-700 bg-amber-50 border-amber-200', 
        label: 'Pending' 
      },
      'FAILED': { 
        icon: FiXCircle, 
        color: 'text-rose-700 bg-rose-50 border-rose-200', 
        label: 'Failed' 
      },
      'REFUND': { 
        icon: FiArrowUp, 
        color: 'text-blue-700 bg-blue-50 border-blue-200', 
        label: 'Refunded' 
      },
      'REFUNDED': { 
        icon: FiArrowUp, 
        color: 'text-blue-700 bg-blue-50 border-blue-200', 
        label: 'Refunded' 
      }
    };
    const config = statusConfig[status?.toUpperCase()] || statusConfig['PENDING'];
    const Icon = config.icon;
    
    return (
      <span className={`
        inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border
        ${config.color}
      `}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  // Transaction type badge
  const TypeBadge = ({ type }) => {
    const typeConfig = {
      'Batch': { icon: FiTag, color: 'text-purple-700 bg-purple-50 border-purple-200' },
      'Course': { icon: FiTag, color: 'text-blue-700 bg-blue-50 border-blue-200' },
      'Book': { icon: FiTag, color: 'text-amber-700 bg-amber-50 border-amber-200' },
      'Boost': { icon: FiTrendingUp, color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
      'Banner': { icon: FiTag, color: 'text-rose-700 bg-rose-50 border-rose-200' },
      'DonateToExpert': { icon: FiDollarSign, color: 'text-indigo-700 bg-indigo-50 border-indigo-200' }
    };
    const config = typeConfig[type] || { icon: FiTag, color: 'text-gray-700 bg-gray-50 border-gray-200' };
    const Icon = config.icon;
    
    return (
      <span className={`
        inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border
        ${config.color}
      `}>
        <Icon className="w-3.5 h-3.5" />
        {type || 'N/A'}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-5xl bg-white rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-6 py-4 border-b border-gray-100 flex-shrink-0 bg-gradient-to-r from-gray-50/50 to-white rounded-t-2xl">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 rounded-xl">
              <FiDollarSign className="w-5 h-5 text-[#715EFE]" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-800 truncate">
                {userInfo?.fullName ? `${userInfo.fullName}'s ` : ""}{title}
              </h2>
              <p className="text-xs text-gray-400 hidden sm:block">
                {transactions?.length || 0} transactions • {total || 0} total
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#715EFE] bg-white border border-[#715EFE] rounded-lg hover:bg-[#715EFE] hover:text-white transition-all duration-200"
              onClick={handleDownloadPDF}
            >
              <FiDownload className="w-4 h-4" />
              <span className="hidden sm:inline">Export PDF</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
              aria-label="Close"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* User Info Banner */}
        {userInfo && (
          <div className="px-6 py-3 bg-gray-50/80 border-b border-gray-100 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 flex items-center justify-center">
              <span className="text-sm font-bold text-[#715EFE]">
                {userInfo.fullName?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate">
                {userInfo.fullName || 'Unknown User'}
              </p>
              {userInfo.email && (
                <p className="text-xs text-gray-400 truncate">
                  {userInfo.email}
                </p>
              )}
            </div>
            {userInfo.createdAt && (
              <span className="text-xs text-gray-400 flex-shrink-0">
                Joined {moment(userInfo.createdAt).format('MMM D, YYYY')}
              </span>
            )}
          </div>
        )}

        {/* Table Container */}
        <div className="flex-1 overflow-y-auto p-1">
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full table-auto text-sm">
              <thead className="bg-gray-50/80 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                <tr>
                  {tableHeaders.map((header, index) => {
                    const headerIcons = {
                      'S.No.': null,
                      'Transaction ID': FiTag,
                      'Payment Type': FiTag,
                      'User Name': FiUser,
                      'Date & Time': FiClock,
                      'Status': FiCheckCircle,
                      'Amount': FiDollarSign
                    };
                    const Icon = headerIcons[header];
                    return (
                      <th key={index} className="px-4 py-3 text-left whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          {Icon && <Icon className="w-3.5 h-3.5 text-gray-400" />}
                          <span>{header}</span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-50">
                {transactions && transactions.length > 0 ? (
                  transactions.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50/70 transition-colors duration-150 group">
                      <td className="px-4 py-3 text-sm text-gray-400">
                        {(pageNo - 1) * size + index + 1}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 flex items-center justify-center">
                            <FiTag className="w-3.5 h-3.5 text-[#715EFE]" />
                          </div>
                          <span className="text-xs font-mono text-gray-600">
                            #{item.id || item._id?.slice(-6) || '-'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <TypeBadge type={item.type} />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {item.user?.fullName || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-700 whitespace-nowrap">
                            {moment(item.createdAt).format('MMM D, YYYY')}
                          </span>
                          <span className="text-xs text-gray-400">
                            {moment(item.createdAt).format('h:mm A')}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={item.status} />
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-emerald-600">
                        ${item.amount?.toFixed(2) || '0.00'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableHeaders.length} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center justify-center gap-3">
                        <div className="p-4 bg-gray-50 rounded-full">
                          <FiDollarSign className="w-10 h-10 text-gray-300" />
                        </div>
                        <p className="text-sm font-medium text-gray-500">No transactions found</p>
                        <p className="text-xs text-gray-400">No payment records available for this user</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer with Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-3 border-t border-gray-100 flex-shrink-0 bg-gray-50/50 rounded-b-2xl">
          <div className="text-sm text-gray-500">
            Showing{" "}
            <span className="font-medium">
              {Math.min((pageNo - 1) * size + 1, total)}
            </span>{" "}
            to{" "}
            <span className="font-medium">
              {Math.min(pageNo * size, total)}
            </span>{" "}
            of <span className="font-medium">{total}</span> entries
          </div>
          
          {total > size && (
            <ReactPaginate
              previousLabel={'Prev'}
              nextLabel={'Next'}
              breakLabel={'...'}
              breakClassName={'px-2 text-gray-400'}
              pageCount={Math.ceil(total / size)}
              marginPagesDisplayed={1}
              pageRangeDisplayed={2}
              onPageChange={handlePageClick}
              containerClassName={'flex items-center gap-1'}
              pageClassName={'px-2'}
              pageLinkClassName={'px-3 py-1 text-sm text-gray-600 rounded-md hover:bg-[#715EFE]/10 hover:text-[#715EFE] transition-colors duration-200'}
              previousClassName={'px-2'}
              previousLinkClassName={'px-3 py-1 text-sm text-gray-600 rounded-md hover:bg-[#715EFE]/10 hover:text-[#715EFE] transition-colors duration-200'}
              nextClassName={'px-2'}
              nextLinkClassName={'px-3 py-1 text-sm text-gray-600 rounded-md hover:bg-[#715EFE]/10 hover:text-[#715EFE] transition-colors duration-200'}
              activeClassName={'bg-[#715EFE] text-white rounded-md'}
              activeLinkClassName={'bg-[#715EFE] text-white px-3 py-1 rounded-md'}
              disabledClassName={'opacity-50 cursor-not-allowed'}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default UserPaymentDetails;